import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { styled } from "@glitz/react";
import Base, { ContentWidth } from "../components/basic/Base";
import Loader from "../components/basic/Loader";
import Button from "../components/basic/Button";
import { useViewport } from "../hooks/useViewport";
import {
  Theme,
  Size,
  spacing,
  fontSize,
  fontWeight,
  green,
  darkGrey,
  grey,
  lightGrey,
  black,
  white,
} from "../theme";

const API_BASE = process.env.REACT_APP_API_URL || "";

/** Måste matcha värden i server/packages.js */
const PACKAGE_SPORTS = ["triathlon", "running", "cycling", "skiing"] as const;
type PackageSport = (typeof PACKAGE_SPORTS)[number];

const SPORT_LABELS: Record<PackageSport, string> = {
  triathlon: "Triathlon",
  running: "Löpning",
  cycling: "Cykel",
  skiing: "Längdskidor",
};

type PackageSummary = {
  slug: string;
  name: string;
  tagline: string;
  totalTimeHours: number;
  packagePrice?: number;
  sport?: PackageSport;
};

type SortKey = "name" | "time" | "price";

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "name", label: "Namn A–Ö" },
  { value: "time", label: "Tid (kortast först)" },
  { value: "price", label: "Pris (lägst först)" },
];

function isPackageSport(v: string | null): v is PackageSport {
  return v != null && (PACKAGE_SPORTS as readonly string[]).includes(v);
}

const filterChipInactive = {
  cursor: "pointer" as const,
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  padding: { x: spacing.s, y: spacing.xs },
  borderRadius: "999px",
  border: { xy: { style: "solid" as const, color: grey, width: "2px" } },
  backgroundColor: white,
  color: black,
  transitionProperty: "background-color, border-color, color" as const,
  transitionDuration: "0.15s",
};

const filterChipActive = {
  cursor: "pointer" as const,
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  padding: { x: spacing.s, y: spacing.xs },
  borderRadius: "999px",
  border: { xy: { style: "solid" as const, color: green, width: "2px" } },
  backgroundColor: green,
  color: white,
  transitionProperty: "background-color, border-color, color" as const,
  transitionDuration: "0.15s",
};

const PackagesPage: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useViewport();
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState<PackageSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>("name");

  const sportParam = searchParams.get("sport");
  const activeSport: PackageSport | "all" =
    isPackageSport(sportParam) ? sportParam : "all";

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/api/packages`)
      .then((r) => {
        if (!r.ok) throw new Error("Kunde inte hämta paket");
        return r.json();
      })
      .then((data: PackageSummary[]) => setList(Array.isArray(data) ? data : []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filteredSorted = useMemo(() => {
    let rows = list;
    if (activeSport !== "all") {
      rows = rows.filter((p) => p.sport === activeSport);
    }
    const copy = [...rows];
    copy.sort((a, b) => {
      if (sort === "name") {
        return a.name.localeCompare(b.name, "sv", { sensitivity: "base" });
      }
      if (sort === "time") {
        return a.totalTimeHours - b.totalTimeHours;
      }
      const pa = a.packagePrice != null && Number.isFinite(a.packagePrice) ? a.packagePrice : Infinity;
      const pb = b.packagePrice != null && Number.isFinite(b.packagePrice) ? b.packagePrice : Infinity;
      return pa - pb;
    });
    return copy;
  }, [list, activeSport, sort]);

  const setSportFilter = (next: PackageSport | "all") => {
    if (next === "all") {
      searchParams.delete("sport");
      setSearchParams(searchParams, { replace: true });
    } else {
      setSearchParams({ sport: next }, { replace: true });
    }
  };

  if (loading) return <Loader ariaLabel="Laddar paket" />;
  if (error) return <ErrorMsg>{error}</ErrorMsg>;

  return (
    <PageWrap>
      <Base contentWidth={ContentWidth.Normal}>
        <Header>
          <Title>Energipaket</Title>
          <Lead>
            Färdiga energikit anpassade efter längd och typ av lopp. Filtrera på sport och
            bygg ditt paket med pris och kolhydratsplan i realtid.
          </Lead>
        </Header>

        <Toolbar css={!isMobile ? toolbarDesktop : undefined}>
          <FilterRow>
            <FilterLabel>Typ av lopp</FilterLabel>
            <ChipRow>
              <FilterChip
                type="button"
                css={activeSport === "all" ? filterChipActive : filterChipInactive}
                onClick={() => setSportFilter("all")}
              >
                Alla
              </FilterChip>
              {PACKAGE_SPORTS.map((id) => (
                <FilterChip
                  key={id}
                  type="button"
                  css={activeSport === id ? filterChipActive : filterChipInactive}
                  onClick={() => setSportFilter(id)}
                >
                  {SPORT_LABELS[id]}
                </FilterChip>
              ))}
            </ChipRow>
          </FilterRow>
          <SortRow>
            <SortLabel htmlFor="package-sort">Sortera</SortLabel>
            <SortSelect
              id="package-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </SortSelect>
          </SortRow>
        </Toolbar>

        {filteredSorted.length === 0 ? (
          <EmptyState>Inga paket matchar filtret.</EmptyState>
        ) : (
          <CardGrid>
            {filteredSorted.map((pkg) => (
              <Card key={pkg.slug}>
                {pkg.sport != null && isPackageSport(pkg.sport) && (
                  <SportBadge>{SPORT_LABELS[pkg.sport]}</SportBadge>
                )}
                <CardTitle>{pkg.name}</CardTitle>
                <CardTagline>{pkg.tagline}</CardTagline>
                <CardMeta>
                  <span>{pkg.totalTimeHours} h</span>
                  {pkg.packagePrice != null && Number.isFinite(pkg.packagePrice) && (
                    <span>från {Math.round(pkg.packagePrice)} kr</span>
                  )}
                </CardMeta>
                <CardActions>
                  <Button
                    as={Theme.Primary}
                    size={Size.Medium}
                    fullWidth
                    onClick={() => navigate(`/paket/${pkg.slug}`)}
                  >
                    Bygg paket
                  </Button>
                </CardActions>
              </Card>
            ))}
          </CardGrid>
        )}
      </Base>
    </PageWrap>
  );
};

export default PackagesPage;

const PageWrap = styled.div({
  padding: { xy: spacing.xxxl },
  flex: { grow: 1 },
});

const Header = styled.header({
  marginBottom: spacing.xl,
});

const Title = styled.h1({
  fontSize: fontSize.xxl,
  fontWeight: fontWeight.heavy,
  margin: { bottom: spacing.s },
  color: black,
});

const Lead = styled.p({
  fontSize: fontSize.m,
  lineHeight: "1.55",
  color: darkGrey,
  maxWidth: "720px",
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
});

const Toolbar = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  marginBottom: spacing.xl,
});

const toolbarDesktop = {
  flexDirection: "row" as const,
  flexWrap: "wrap" as const,
  alignItems: "flex-end" as const,
  justifyContent: "space-between" as const,
};

const FilterRow = styled.div({
  flex: { grow: 1 },
  minWidth: "240px",
});

const FilterLabel = styled.span({
  display: "block",
  fontSize: fontSize.xs,
  fontWeight: fontWeight.bold,
  color: darkGrey,
  marginBottom: spacing.xs,
});

const ChipRow = styled.div({
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.xs,
});

const FilterChip = styled.button({});

const SortRow = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
  minWidth: "200px",
});

const SortLabel = styled.label({
  fontSize: fontSize.xs,
  fontWeight: fontWeight.bold,
  color: darkGrey,
});

const SortSelect = styled.select({
  padding: { x: spacing.s, y: spacing.xs },
  fontSize: fontSize.s,
  border: { xy: { style: "solid", color: black, width: "1px" } },
  borderRadius: "4px",
  backgroundColor: white,
  ":focus": {
    outline: { style: "none" },
    border: { xy: { style: "solid", color: green, width: "1px" } },
  },
});

const CardGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: spacing.l,
});

const Card = styled.article({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  padding: { xy: spacing.l },
  backgroundColor: lightGrey,
  borderRadius: "8px",
  border: { xy: { style: "solid", color: grey, width: "1px" } },
});

const SportBadge = styled.span({
  alignSelf: "flex-start",
  fontSize: fontSize.xxs,
  fontWeight: fontWeight.bold,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: green,
  marginBottom: spacing.xs,
});

const CardTitle = styled.h2({
  fontSize: fontSize.ml,
  fontWeight: fontWeight.bold,
  margin: { bottom: spacing.xs },
  color: black,
});

const CardTagline = styled.p({
  fontSize: fontSize.s,
  lineHeight: "1.5",
  color: darkGrey,
  margin: { bottom: spacing.m },
  flex: { grow: 1 },
});

const CardMeta = styled.div({
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.s,
  fontSize: fontSize.xs,
  color: darkGrey,
  marginBottom: spacing.m,
});

const CardActions = styled.div({
  marginTop: "auto",
});

const EmptyState = styled.p({
  textAlign: "center",
  padding: { xy: spacing.xxl },
  color: darkGrey,
  fontSize: fontSize.m,
});

const ErrorMsg = styled.p({
  textAlign: "center",
  padding: { xy: spacing.xxxxl },
  color: "#CC0000",
  fontSize: fontSize.l,
});
